import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ProfileLink from '../components/ProfileLink';
import TeamStatsLink from '../components/TeamStatsLink';
import { useAuth } from '../contexts/AuthContext';
import { useLeaderboardCelebration } from '../contexts/LeaderboardCelebrationContext';
import { usePlayerCache } from '../contexts/PlayerCacheContext';
import { doc, collection, onSnapshot, updateDoc, setDoc, deleteDoc, serverTimestamp, Timestamp, runTransaction, increment } from 'firebase/firestore';
import { db } from '../firebase/config';
import * as matchApi from '../api/matchApi';
import * as readApi from '../api/readApi';
import { invalidateCache, invalidateStatisticsCache, invalidatePlayerProfileCache, CACHE_KEYS } from '../api/readApi';
import DataFetchOverlay from '../components/DataFetchOverlay';
import GameInfoModal from '../components/GameInfoModal';
import MatchUpdateHistoryModal from '../components/MatchUpdateHistoryModal';
import MatchReactionsModal from '../components/MatchReactionsModal';
import { formatDateTime } from '../utils/dateFormat';
import { getUserDisplayName } from '../utils/nameFormat';
import '../services/matchUpdateLogService';
import { getNextServer, isGameWon, isMatchWon, validateScoreUpdate } from '../utils/scoringEngine';
import { getNextServerDoublesByPlayerId } from '../utils/scoringEnginePlayerId';
import { sendMatchStarted, sendMatchCompleted, isEmailConfigured } from '../services/emailService';
import { REACTION_EMOJIS } from '../constants/reactions';
import { DEFAULT_SCORING_CONFIG, getScoringOptionsForMatchType } from '../constants/scoring';
import { useAlert } from '../contexts/AlertContext';
import './MatchScoring.css';

export default function MatchScoring() {
  const { matchId } = useParams();
  const { currentUser, userProfile } = useAuth();
  const { showAlert, showConfirmPromise, showToast } = useAlert();
  const celebration = useLeaderboardCelebration();
  const { getPlayers, getPlayerName, getPlayerApartmentFlat } = usePlayerCache();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [player1Name, setPlayer1Name] = useState('');
  const [player2Name, setPlayer2Name] = useState('');
  const [refereeName, setRefereeName] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  
  // Game scores (best of 3) - 21 points to win
  const [game1, setGame1] = useState({ player1: 0, player2: 0 });
  const [game2, setGame2] = useState({ player1: 0, player2: 0 });
  const [game3, setGame3] = useState({ player1: 0, player2: 0 });
  
  // Service tracking - derived from total points; firstServer/firstServerPlayerId set at 0-0
  // For singles: { currentServer, firstServer, services: [] }
  // For doubles: { currentServer, currentServerPlayerId, firstServer, firstServerPlayerId, services: [] }
  const [game1Service, setGame1Service] = useState({ currentServer: 'player1', firstServer: 'player1', servesInRotation: 0, services: [] });
  const [game2Service, setGame2Service] = useState({ currentServer: 'player1', firstServer: 'player1', servesInRotation: 0, services: [] });
  const [game3Service, setGame3Service] = useState({ currentServer: 'player1', firstServer: 'player1', servesInRotation: 0, services: [] });
  
  // Individual player scores for doubles
  const [game1PlayerScores, setGame1PlayerScores] = useState({});
  const [game2PlayerScores, setGame2PlayerScores] = useState({});
  const [game3PlayerScores, setGame3PlayerScores] = useState({});
  
  // Score history with referee info and timestamps
  const [scoreHistory, setScoreHistory] = useState([]);
  const [undoHistory, setUndoHistory] = useState([]);
  
  const [currentGame, setCurrentGame] = useState(1);
  const [gameWinners, setGameWinners] = useState({ game1: null, game2: null, game3: null });
  const [matchWinner, setMatchWinner] = useState(null);
  const [showGameInfo, setShowGameInfo] = useState({ game1: false, game2: false, game3: false });
  const [pendingScoreUpdate, setPendingScoreUpdate] = useState(null);
  /** Tournament-level: when false, doubles do not require selecting which player scored. */
  const [doublesRequireIndividualScores, setDoublesRequireIndividualScores] = useState(true);
  /** Tournament-level scoring: points to win (11|21) and serves per rotation (2|5) for singles and doubles. */
  const [scoringConfig, setScoringConfig] = useState(DEFAULT_SCORING_CONFIG);
  /** Admin: manual score entry modal { gameNum } or null */
  const [showManualScoreModal, setShowManualScoreModal] = useState(null);
  const [manualScoreP1, setManualScoreP1] = useState('');
  const [manualScoreP2, setManualScoreP2] = useState('');
  /** Reactions: support a team for this match; counts per team per emoji, current user's selections per team */
  const [reactionCounts, setReactionCounts] = useState(() => ({
    player1: Object.fromEntries(REACTION_EMOJIS.map(e => [e, 0])),
    player2: Object.fromEntries(REACTION_EMOJIS.map(e => [e, 0]))
  }));
  const [myReactions, setMyReactions] = useState(() => ({ player1: new Set(), player2: new Set() }));
  const [showReactionsModal, setShowReactionsModal] = useState(false);
  const [venueDisplay, setVenueDisplay] = useState({ venueName: '', courtName: '' });
  // Poll match data via Cloud Function instead of direct Firestore onSnapshot.
  const pollMatch = React.useCallback(async () => {
    if (!matchId || !currentUser) return;
    try {
      const { match: matchData, venueDisplay: vd, tournamentConfig: tc } = await readApi.getMatchForScoring(matchId);
      if (!matchData) {
        setMatch(null);
        setLoading(false);
        return;
      }
      setMatch(matchData);
      setVenueDisplay(vd || { venueName: '', courtName: '' });
      if (tc) {
        setDoublesRequireIndividualScores(tc.doublesRequireIndividualScores !== false);
        if (tc.scoringConfig) {
          setScoringConfig({
            singlesPointsToWin: tc.scoringConfig.singlesPointsToWin ?? 21,
            doublesPointsToWin: tc.scoringConfig.doublesPointsToWin ?? 21,
            singlesServesPerRotation: tc.scoringConfig.singlesServesPerRotation ?? 2,
            doublesServesPerRotation: tc.scoringConfig.doublesServesPerRotation ?? 2
          });
        }
      }

      const playerIds = [];
      if (matchData.matchType === 'doubles') {
        setPlayer1Name(matchData.team1Name || 'Team 1');
        setPlayer2Name(matchData.team2Name || 'Team 2');
        if (matchData.team1Players) playerIds.push(...matchData.team1Players);
        if (matchData.team2Players) playerIds.push(...matchData.team2Players);
      } else {
        if (matchData.player1Id) playerIds.push(matchData.player1Id);
        if (matchData.player2Id) playerIds.push(matchData.player2Id);
      }
      if (matchData.refereeId) playerIds.push(matchData.refereeId);

      getPlayers(playerIds).then(() => {
        if (matchData.matchType === 'doubles') {
            // Doubles: team names shown from matchData.team1Name/team2Name or derived in UI
          } else {
            setPlayer1Name(matchData.player1Id ? getPlayerName(matchData.player1Id) : 'Player 1');
            setPlayer2Name(matchData.player2Id ? getPlayerName(matchData.player2Id) : 'Player 2');
          }
          if (matchData.refereeId) setRefereeName(getPlayerName(matchData.refereeId));
        });

      if (matchData.scores) {
          const s = matchData.scores;
          if (s.game1) setGame1(s.game1);
          if (s.game2) setGame2(s.game2);
          if (s.game3) setGame3(s.game3);
          if (s.currentGame) setCurrentGame(s.currentGame);
          if (s.gameWinners) setGameWinners(s.gameWinners);
          if (s.matchWinner) setMatchWinner(s.matchWinner);
          if (s.game1Service) {
            setGame1Service({ ...s.game1Service, firstServer: s.game1Service.firstServer ?? s.game1Service.currentServer, firstServerPlayerId: s.game1Service.firstServerPlayerId ?? s.game1Service.currentServerPlayerId, servesInRotation: s.game1Service.servesInRotation ?? 0 });
          }
          if (s.game2Service) {
            setGame2Service({ ...s.game2Service, firstServer: s.game2Service.firstServer ?? s.game2Service.currentServer, firstServerPlayerId: s.game2Service.firstServerPlayerId ?? s.game2Service.currentServerPlayerId, servesInRotation: s.game2Service.servesInRotation ?? 0 });
          }
          if (s.game3Service) {
            setGame3Service({ ...s.game3Service, firstServer: s.game3Service.firstServer ?? s.game3Service.currentServer, firstServerPlayerId: s.game3Service.firstServerPlayerId ?? s.game3Service.currentServerPlayerId, servesInRotation: s.game3Service.servesInRotation ?? 0 });
          }
          if (s.game1PlayerScores) setGame1PlayerScores(s.game1PlayerScores);
          if (s.game2PlayerScores) setGame2PlayerScores(s.game2PlayerScores);
          if (s.game3PlayerScores) setGame3PlayerScores(s.game3PlayerScores);
          if (s.scoreHistory) setScoreHistory(s.scoreHistory);
        }
      setLoading(false);
    } catch (err) {
      console.error('Match poll error:', err);
      setLoading(false);
    }
  }, [matchId, currentUser, getPlayers, getPlayerName]);

  useEffect(() => {
    if (!matchId || !currentUser) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    pollMatch();
    const interval = setInterval(pollMatch, 2500);
    return () => clearInterval(interval);
  }, [matchId, currentUser, pollMatch]);

  // Record unique view: one increment per unique user (first time they view this match page)
  useEffect(() => {
    if (!matchId || !currentUser?.uid) return;
    const viewRef = doc(db, 'matches', matchId, 'views', currentUser.uid);
    const matchRef = doc(db, 'matches', matchId);
    let didIncrement = false;
    runTransaction(db, async (transaction) => {
      const viewDoc = await transaction.get(viewRef);
      const matchDoc = await transaction.get(matchRef);
      if (!matchDoc.exists()) return;
      if (!viewDoc.exists()) {
        didIncrement = true;
        transaction.set(viewRef, { firstViewedAt: serverTimestamp() });
        transaction.update(matchRef, { viewCount: increment(1), updatedAt: Timestamp.now() });
      }
    })
      .then(() => {
        if (didIncrement) {
          setMatch((prev) => (prev ? { ...prev, viewCount: (prev.viewCount ?? 0) + 1 } : prev));
        }
      })
      .catch((err) => {
        console.warn('Record unique view failed:', err);
      });
  }, [matchId, currentUser?.uid]);

  // Reactions: subscribe to match reactions, aggregate counts per team and current user's selections per team
  useEffect(() => {
    if (!matchId) return;
    const reactionsRef = collection(db, 'matches', matchId, 'reactions');
    const unsubscribe = onSnapshot(reactionsRef, (snapshot) => {
      const counts = {
        player1: { ...Object.fromEntries(REACTION_EMOJIS.map(e => [e, 0])) },
        player2: { ...Object.fromEntries(REACTION_EMOJIS.map(e => [e, 0])) }
      };
      const mine = { player1: new Set(), player2: new Set() };
      snapshot.docs.forEach((d) => {
        const data = d.data();
        const emoji = data.emoji;
        const team = data.team;
        if (REACTION_EMOJIS.includes(emoji) && (team === 'player1' || team === 'player2')) {
          counts[team][emoji] = (counts[team][emoji] ?? 0) + 1;
          if (data.userId === currentUser?.uid) mine[team].add(emoji);
        }
      });
      setReactionCounts(counts);
      setMyReactions(mine);
    }, (err) => {
      console.warn('Reactions snapshot error:', err);
    });
    return () => unsubscribe();
  }, [matchId, currentUser?.uid]);

  /** Revert last score update (pop undo history and restore state). Call on backend persist failure. */
  function revertLastScore() {
    if (undoHistory.length === 0) return;
    const lastUndo = undoHistory[undoHistory.length - 1];
    const gameNum = lastUndo.gameNum;
    const setGameState = gameNum === 1 ? setGame1 : gameNum === 2 ? setGame2 : setGame3;
    const setServiceState = gameNum === 1 ? setGame1Service : gameNum === 2 ? setGame2Service : setGame3Service;
    const setPlayerScoresState = gameNum === 1 ? setGame1PlayerScores : gameNum === 2 ? setGame2PlayerScores : setGame3PlayerScores;
    setGameState(lastUndo.gameState);
    setServiceState(lastUndo.serviceState);
    setPlayerScoresState(lastUndo.playerScoresState);
    setUndoHistory((prev) => prev.slice(0, -1));
    setScoreHistory((prev) => prev.slice(0, -1));
  }

  /** Persist current scores via backend (rules engine validates; audit log line-by-line). On failure optionally revert and show error. */
  async function persistScoresToFirestore(scoresPayload, onRevert) {
    if (!matchId || !scoresPayload) return;
    const actorName = getUserDisplayName({ displayName: userProfile?.displayName || currentUser?.displayName, email: currentUser?.email });
    try {
      await matchApi.matchUpdateScores(matchId, scoresPayload, actorName);
    } catch (err) {
      console.error('Error persisting scores:', err);
      if (typeof onRevert === 'function') onRevert();
      showAlert('Score update failed: ' + (err?.message || err) + '. Changes reverted.', { variant: 'error' });
    }
  }

  async function toggleReaction(emoji, team) {
    if (!matchId || !currentUser?.uid || (team !== 'player1' && team !== 'player2')) return;
    const emojiIndex = String(REACTION_EMOJIS.indexOf(emoji));
    if (emojiIndex === '-1') return;
    const docId = `${currentUser.uid}_${emojiIndex}_${team}`;
    const reactionRef = doc(db, 'matches', matchId, 'reactions', docId);
    const matchRef = doc(db, 'matches', matchId);
    if (myReactions[team]?.has(emoji)) {
      try {
        await deleteDoc(reactionRef);
        await updateDoc(matchRef, {
          [`reactionCounts.${team}.${emojiIndex}`]: increment(-1),
          updatedAt: Timestamp.now()
        });
      } catch (err) {
        console.warn('Remove reaction failed:', err);
      }
    } else {
      const displayName = getUserDisplayName({ displayName: userProfile?.displayName || currentUser.displayName, email: currentUser.email });
      try {
        await setDoc(reactionRef, {
          userId: currentUser.uid,
          emoji,
          emojiIndex,
          team,
          displayName: displayName || 'Someone',
          createdAt: serverTimestamp()
        });
        await updateDoc(matchRef, {
          [`reactionCounts.${team}.${emojiIndex}`]: increment(1),
          updatedAt: Timestamp.now()
        });
      } catch (err) {
        console.warn('Add reaction failed:', err);
      }
    }
  }

  async function handleStartMatch() {
    const matchPlayerIds = new Set();
    if (match?.matchType === 'doubles') {
      (match.team1Players || []).forEach(id => matchPlayerIds.add(id));
      (match.team2Players || []).forEach(id => matchPlayerIds.add(id));
    } else {
      if (match?.player1Id) matchPlayerIds.add(match.player1Id);
      if (match?.player2Id) matchPlayerIds.add(match.player2Id);
    }
    const isRefereeHere = match.refereeId === currentUser?.uid;
    const isAdminHere = userProfile?.isAdmin || userProfile?.isSuperAdmin;
    if (!isRefereeHere && !isAdminHere) {
      showAlert('Only the referee or an admin can start the match.', { variant: 'warning' });
      return;
    }
    const startOk = await showConfirmPromise('Start the match? This will mark the match as in progress.', { title: 'Start match' });
    if (!startOk) return;

    const previousMatch = match;
    try {
      const refereeName = getUserDisplayName({ displayName: userProfile?.displayName || currentUser.displayName, email: currentUser.email });
      setMatch((prev) => ({
        ...prev,
        status: 'in-progress',
        startedAt: Timestamp.now(),
        refereeId: prev.refereeId || currentUser.uid,
        refereeName: prev.refereeName || refereeName,
      }));
      if (!match.refereeId) setRefereeName(refereeName);
      await matchApi.matchStart(matchId, refereeName);

      if (await isEmailConfigured()) {
        const playerIds = match.matchType === 'doubles'
          ? [...(match.team1Players || []), ...(match.team2Players || [])]
          : [match.player1Id, match.player2Id].filter(Boolean);
        const players = await getPlayers(playerIds);
        const emails = [...new Set(Object.values(players).map(p => p?.email).filter(Boolean))];
        const summary = match.matchType === 'doubles'
          ? `${match.team1Name || 'Team 1'} vs ${match.team2Name || 'Team 2'}`
          : `${match.player1Name || getPlayerName(match.player1Id) || 'Player 1'} vs ${match.player2Name || getPlayerName(match.player2Id) || 'Player 2'}`;
        for (const email of emails) {
          await sendMatchStarted(email, summary).catch(() => {});
        }
      }

      showToast('Match started!', { variant: 'success' });
    } catch (err) {
      setMatch(previousMatch);
      if (match?.refereeId !== previousMatch?.refereeId) setRefereeName(previousMatch?.refereeName || '');
      console.error('Error starting match:', err);
      showAlert('Error starting match: ' + (err?.message || err), { variant: 'error' });
    }
  }

  async function handleAssignSelfAsReferee() {
    const matchPlayerIds = new Set();
    if (match?.matchType === 'doubles') {
      (match.team1Players || []).forEach(id => matchPlayerIds.add(id));
      (match.team2Players || []).forEach(id => matchPlayerIds.add(id));
    } else {
      if (match?.player1Id) matchPlayerIds.add(match.player1Id);
      if (match?.player2Id) matchPlayerIds.add(match.player2Id);
    }
    if (matchPlayerIds.has(currentUser.uid)) {
      showAlert('Players in this match cannot be the referee. Only someone not playing can assign themselves as referee.', { variant: 'warning' });
      return;
    }

    try {
      const newRefereeName = getUserDisplayName({ displayName: userProfile?.displayName || currentUser.displayName, email: currentUser.email });
      await matchApi.matchAssignReferee(matchId, newRefereeName);

      setMatch(prev => ({
        ...prev,
        refereeId: currentUser.uid,
        refereeName: newRefereeName
      }));
      setRefereeName(newRefereeName);
      showToast('You are now the referee for this match.', { variant: 'success' });
    } catch (err) {
      console.error('Error assigning referee:', err);
      showAlert('Error: ' + err.message, { variant: 'error' });
    }
  }

  /** Set who serves first for a game (toss result). Only when game score is 0-0. */
  async function handleSetFirstServer(gameNum, firstServer) {
    if (!canScore || !match?.startedAt) return;
    const gameState = gameNum === 1 ? game1 : gameNum === 2 ? game2 : game3;
    if (gameState.player1 !== 0 || gameState.player2 !== 0) {
      showAlert('First server can only be set when the game score is 0-0.', { variant: 'warning' });
      return;
    }
    const setServiceState = gameNum === 1 ? setGame1Service : gameNum === 2 ? setGame2Service : setGame3Service;
    const firstServerPlayerId = match?.matchType === 'doubles' && (match?.team1Players && match?.team2Players)
      ? (firstServer === 'player2' ? match.team2Players[0] : match.team1Players[0])
      : null;
    const baseService = { currentServer: firstServer, firstServer, servesInRotation: 0, services: [] };
    const newServiceState = match?.matchType === 'doubles' && match?.team1Players && match?.team2Players
      ? {
          ...baseService,
          currentServerPlayerId: firstServerPlayerId,
          firstServerPlayerId: firstServerPlayerId
        }
      : baseService;
    setServiceState(newServiceState);
    const payload = {
      game1,
      game2,
      game3,
      currentGame,
      gameWinners,
      matchWinner,
      game1Service: gameNum === 1 ? newServiceState : game1Service,
      game2Service: gameNum === 2 ? newServiceState : game2Service,
      game3Service: gameNum === 3 ? newServiceState : game3Service,
      game1PlayerScores,
      game2PlayerScores,
      game3PlayerScores,
      scoreHistory
    };
    const actorName = getUserDisplayName({ displayName: userProfile?.displayName || currentUser?.displayName, email: currentUser?.email });
    try {
      await matchApi.matchSetFirstServer(matchId, payload, actorName);
    } catch (err) {
      console.error('Error setting first server:', err);
    }
  }

  function confirmScoreUpdate(gameNum, player, increment) {
    // For doubles, if increment > 0 and tournament requires individual scores, show player selection modal
    if (match?.matchType === 'doubles' && increment > 0 && doublesRequireIndividualScores) {
      const teamPlayers = player === 'player1' ? match.team1Players : match.team2Players;
      if (teamPlayers && teamPlayers.length > 1) {
        setPendingScoreUpdate({ gameNum, player, increment, needsPlayerSelection: true });
        return;
      } else if (teamPlayers && teamPlayers.length === 1) {
        setPendingScoreUpdate({ gameNum, player, increment, scoringPlayerId: teamPlayers[0] });
        return;
      }
    }
    setPendingScoreUpdate({ gameNum, player, increment });
  }

  function executeScoreUpdate() {
    if (!pendingScoreUpdate) return;
    
    // If needs player selection, don't execute yet
    if (pendingScoreUpdate.needsPlayerSelection) {
      return;
    }
    
    const { gameNum, player, increment, scoringPlayerId } = pendingScoreUpdate;
    updateScore(gameNum, player, increment, scoringPlayerId);
    setPendingScoreUpdate(null);
  }

  function updateScore(gameNum, player, increment, scoringPlayerId = null) {
    const gameState = gameNum === 1 ? game1 : gameNum === 2 ? game2 : game3;
    const setGameState = gameNum === 1 ? setGame1 : gameNum === 2 ? setGame2 : setGame3;
    const serviceState = gameNum === 1 ? game1Service : gameNum === 2 ? game2Service : game3Service;
    const setServiceState = gameNum === 1 ? setGame1Service : gameNum === 2 ? setGame2Service : setGame3Service;
    const playerScoresState = gameNum === 1 ? game1PlayerScores : gameNum === 2 ? game2PlayerScores : game3PlayerScores;
    const setPlayerScoresState = gameNum === 1 ? setGame1PlayerScores : gameNum === 2 ? setGame2PlayerScores : setGame3PlayerScores;
    
    const otherPlayer = player === 'player1' ? 'player2' : 'player1';
    
    // Validate score update
    const validation = validateScoreUpdate(gameState[player], gameState[player] + increment, gameState[otherPlayer]);
    if (!validation.valid && increment > 0) {
      showAlert(validation.reason, { variant: 'warning' });
      return;
    }
    
    const matchType = match?.matchType || 'singles';
    const scoringOptions = getScoringOptionsForMatchType(scoringConfig, matchType);
    const currentPlayer1Score = gameState.player1;
    const currentPlayer2Score = gameState.player2;
    const currentGameResult = isGameWon(currentPlayer1Score, currentPlayer2Score, matchType, { pointsToWin: scoringOptions.pointsToWin });
    if (currentGameResult.won && increment > 0) {
      showAlert('Game is already completed', { variant: 'warning' });
      return;
    }
    
    // Save current state for undo (deep copy service state so services array is not mutated when we add/update points)
    const undoState = {
      gameNum,
      gameState: { ...gameState },
      serviceState: {
        ...serviceState,
        services: (serviceState.services || []).map(s => ({ ...s }))
      },
      playerScoresState: { ...playerScoresState },
      timestamp: new Date()
    };
    setUndoHistory(prev => [...prev, undoState]);
    
    const newScore = Math.max(0, gameState[player] + increment);
    setGameState({ ...gameState, [player]: newScore });

    // Add to score history with referee info
    const scoreEntry = {
      gameNum,
      player,
      oldScore: gameState[player],
      newScore,
      increment,
      scoringPlayerId: scoringPlayerId || null, // Track which individual player scored (for doubles)
      refereeId: currentUser.uid,
      refereeName: getUserDisplayName({ displayName: userProfile?.displayName || currentUser.displayName, email: currentUser.email }),
      timestamp: Timestamp.now()
    };
    setScoreHistory(prev => [...prev, scoreEntry]);
    
    let updatedServiceStateForGame = serviceState;
    let newPlayerScoresStateForGame = playerScoresState;
    
    // For doubles, add score to individual player
    if (match?.matchType === 'doubles' && increment > 0 && scoringPlayerId) {
      newPlayerScoresStateForGame = { ...playerScoresState, [scoringPlayerId]: (playerScoresState[scoringPlayerId] || 0) + 1 };
      setPlayerScoresState(prev => ({
        ...prev,
        [scoringPlayerId]: (prev[scoringPlayerId] || 0) + 1
      }));
    } else if (match?.matchType === 'doubles' && increment < 0 && scoringPlayerId) {
      newPlayerScoresStateForGame = { ...playerScoresState, [scoringPlayerId]: Math.max(0, (playerScoresState[scoringPlayerId] || 0) + increment) };
      setPlayerScoresState(prev => ({
        ...prev,
        [scoringPlayerId]: Math.max(0, (prev[scoringPlayerId] || 0) + increment)
      }));
    }

    // Track service and individual player points using proper ITTF rules
    if (increment > 0) {
      const services = [...(serviceState.services || [])];

      if (match?.matchType === 'doubles') {
        const player1Score = player === 'player1' ? newScore : gameState.player1;
        const player2Score = player === 'player2' ? newScore : gameState.player2;
        const opts = getScoringOptionsForMatchType(scoringConfig, 'doubles');
        const isDeuce = player1Score >= opts.pointsToWin - 1 && player2Score >= opts.pointsToWin - 1;
        
        const firstServer = serviceState.firstServer ?? serviceState.currentServer;
        const firstServerPlayerId = serviceState.firstServerPlayerId ?? (serviceState.currentServer === 'player2' ? match.team2Players?.[0] : match.team1Players?.[0]) ?? null;
        const currentServerPlayerId = serviceState.currentServerPlayerId ?? firstServerPlayerId;
        
        const nextServerInfo = getNextServerDoublesByPlayerId(
          player1Score,
          player2Score,
          firstServer,
          firstServerPlayerId,
          match.team1Players || [],
          match.team2Players || [],
          { pointsToWin: opts.pointsToWin, servesPerRotation: opts.servesPerRotation }
        );
        
        // Determine winner and loser player IDs
        const winnerPlayerId = scoringPlayerId || (player === 'player1' ? (match.team1Players?.[0] || null) : (match.team2Players?.[0] || null));
        const loserTeamPlayers = player === 'player1' ? match.team2Players : match.team1Players;
        const loserPlayerId = loserTeamPlayers?.[0] || null; // For now, use first player of losing team
        
        // Check if we need to start a new service
        const lastService = services.length > 0 ? services[services.length - 1] : null;
        const isNewService = isDeuce ||
          !lastService || 
          lastService.server !== serviceState.currentServer ||
          lastService.serverPlayerId !== currentServerPlayerId ||
          nextServerInfo.shouldSwitch;
        
        // Calculate historic points for the server at the start of this service
        const serverPointsAtService = serviceState.currentServer === 'player1' 
          ? (gameState.player1 - (player === 'player1' ? 1 : 0))
          : (gameState.player2 - (player === 'player2' ? 1 : 0));
        
        if (isNewService) {
          // Start a new service (5 serves, or 1 serve in deuce)
          services.push({
            server: serviceState.currentServer,
            serverPlayerId: currentServerPlayerId,
            receiverPlayerId: nextServerInfo.receiverPlayerId,
            points: 1,
            player: player, // team who won the point
            winnerPlayerId: winnerPlayerId,
            loserPlayerId: loserPlayerId,
            servesCompleted: 1,
            serverPointsAtService: serverPointsAtService,
            refereeId: currentUser.uid,
            refereeName: getUserDisplayName({ displayName: userProfile?.displayName || currentUser.displayName, email: currentUser.email }),
            timestamp: Timestamp.now()
          });
        } else {
          // Continue current service (same server, same rotation)
          lastService.points++;
          lastService.servesCompleted = (lastService.servesCompleted || 0) + 1;
          // Update winner/loser based on who scored
          lastService.player = player;
          lastService.winnerPlayerId = winnerPlayerId;
          lastService.loserPlayerId = loserPlayerId;
        }
        
        // Update service state (keep firstServer/firstServerPlayerId for next call)
        const updatedServiceState = {
          ...serviceState,
          firstServer: serviceState.firstServer ?? serviceState.currentServer,
          firstServerPlayerId: serviceState.firstServerPlayerId ?? serviceState.currentServerPlayerId,
          services,
          currentServer: nextServerInfo.server,
          currentServerPlayerId: nextServerInfo.serverPlayerId,
          servesInRotation: nextServerInfo.servesInRotation
        };
        updatedServiceStateForGame = updatedServiceState;
        setServiceState(updatedServiceState);
      } else {
        const player1Score = player === 'player1' ? newScore : gameState.player1;
        const player2Score = player === 'player2' ? newScore : gameState.player2;
        const opts = getScoringOptionsForMatchType(scoringConfig, 'singles');
        const firstServer = serviceState.firstServer ?? serviceState.currentServer;
        const isDeuce = player1Score >= opts.pointsToWin - 1 && player2Score >= opts.pointsToWin - 1;
        
        const nextServerInfo = getNextServer(player1Score, player2Score, firstServer, { pointsToWin: opts.pointsToWin, servesPerRotation: opts.servesPerRotation });
        
        const lastService = services.length > 0 ? services[services.length - 1] : null;
        const isNewService = isDeuce ||
          !lastService || 
          lastService.server !== serviceState.currentServer ||
          nextServerInfo.shouldSwitch;
        
        // Calculate historic points for the server at the start of this service
        const serverPointsAtService = serviceState.currentServer === 'player1' 
          ? (gameState.player1 - (player === 'player1' ? 1 : 0))
          : (gameState.player2 - (player === 'player2' ? 1 : 0));
        
        if (isNewService) {
          // Start a new service (5 serves, or 1 serve in deuce)
          const winner = player;
          const loser = otherPlayer;
          services.push({
            server: serviceState.currentServer,
            points: 1,
            player: player, // who won the point
            winner: winner,
            loser: loser,
            servesCompleted: 1,
            serverPointsAtService: serverPointsAtService,
            refereeId: currentUser.uid,
            refereeName: getUserDisplayName({ displayName: userProfile?.displayName || currentUser.displayName, email: currentUser.email }),
            timestamp: Timestamp.now()
          });
        } else {
          // Continue current service (same server, same rotation)
          lastService.points++;
          lastService.servesCompleted = (lastService.servesCompleted || 0) + 1;
          // Update winner/loser based on who scored
          lastService.player = player;
          lastService.winner = player;
          lastService.loser = otherPlayer;
        }
        
        const updatedServiceState = {
          ...serviceState,
          firstServer: serviceState.firstServer ?? serviceState.currentServer,
          services,
          currentServer: nextServerInfo.server,
          servesInRotation: nextServerInfo.servesInRotation
        };
        updatedServiceStateForGame = updatedServiceState;
        setServiceState(updatedServiceState);
      }

      // Individual player scores are now tracked above when scoreEntry is created
    }

    const player1Score = player === 'player1' ? newScore : gameState.player1;
    const player2Score = player === 'player2' ? newScore : gameState.player2;
    const gameResult = isGameWon(player1Score, player2Score, matchType, { pointsToWin: scoringOptions.pointsToWin });
    
    const newGameWinners = gameResult.won ? { ...gameWinners, [`game${gameNum}`]: gameResult.winner } : gameWinners;
    const matchResult = isMatchWon(newGameWinners);
    const newMatchWinner = (gameResult.won && matchResult.won) ? matchResult.winner : matchWinner;
    if (gameResult.won) {
      setGameWinners(newGameWinners);
      if (matchResult.won) {
        setMatchWinner(matchResult.winner);
      }
    }

    // Build payload and persist immediately so referee updates are stored
    const newGameState = { ...gameState, [player]: newScore };
    const payload = {
      game1: gameNum === 1 ? newGameState : game1,
      game2: gameNum === 2 ? newGameState : game2,
      game3: gameNum === 3 ? newGameState : game3,
      currentGame,
      gameWinners: newGameWinners,
      matchWinner: newMatchWinner,
      game1Service: gameNum === 1 ? updatedServiceStateForGame : game1Service,
      game2Service: gameNum === 2 ? updatedServiceStateForGame : game2Service,
      game3Service: gameNum === 3 ? updatedServiceStateForGame : game3Service,
      game1PlayerScores: gameNum === 1 ? newPlayerScoresStateForGame : game1PlayerScores,
      game2PlayerScores: gameNum === 2 ? newPlayerScoresStateForGame : game2PlayerScores,
      game3PlayerScores: gameNum === 3 ? newPlayerScoresStateForGame : game3PlayerScores,
      scoreHistory: [...scoreHistory, scoreEntry]
    };
    persistScoresToFirestore(payload, revertLastScore);
  }

  async function handleUndo() {
    // Only referee can undo
    if (!isReferee) {
      showAlert('Only the referee can undo scores', { variant: 'warning' });
      return;
    }

    if (undoHistory.length === 0) {
      showAlert('Nothing to undo', { variant: 'info' });
      return;
    }

    const undoOk = await showConfirmPromise('Are you sure you want to undo the last score update?', { title: 'Undo score' });
    if (!undoOk) return;

    const lastUndo = undoHistory[undoHistory.length - 1];
    const gameNum = lastUndo.gameNum;
    
    const setGameState = gameNum === 1 ? setGame1 : gameNum === 2 ? setGame2 : setGame3;
    const setServiceState = gameNum === 1 ? setGame1Service : gameNum === 2 ? setGame2Service : setGame3Service;
    const setPlayerScoresState = gameNum === 1 ? setGame1PlayerScores : gameNum === 2 ? setGame2PlayerScores : setGame3PlayerScores;

    setGameState(lastUndo.gameState);
    setServiceState(lastUndo.serviceState);
    setPlayerScoresState(lastUndo.playerScoresState);
    setUndoHistory(prev => prev.slice(0, -1));
    setScoreHistory(prev => prev.slice(0, -1));

    // Persist reverted state immediately
    const revertedGameState = lastUndo.gameState;
    const revertedServiceState = lastUndo.serviceState;
    const revertedPlayerScoresState = lastUndo.playerScoresState;
    const payload = {
      game1: gameNum === 1 ? revertedGameState : game1,
      game2: gameNum === 2 ? revertedGameState : game2,
      game3: gameNum === 3 ? revertedGameState : game3,
      currentGame,
      gameWinners,
      matchWinner,
      game1Service: gameNum === 1 ? revertedServiceState : game1Service,
      game2Service: gameNum === 2 ? revertedServiceState : game2Service,
      game3Service: gameNum === 3 ? revertedServiceState : game3Service,
      game1PlayerScores: gameNum === 1 ? revertedPlayerScoresState : game1PlayerScores,
      game2PlayerScores: gameNum === 2 ? revertedPlayerScoresState : game2PlayerScores,
      game3PlayerScores: gameNum === 3 ? revertedPlayerScoresState : game3PlayerScores,
      scoreHistory: scoreHistory.slice(0, -1)
    };
    persistScoresToFirestore(payload);
    
    showToast('Score update undone', { variant: 'success' });
  }

  /** Admin only: reset a game to 0-0 and clear its service/history. */
  async function handleResetGame(gameNum) {
    if (!isAdmin) {
      showAlert('Only an admin can reset a game.', { variant: 'warning' });
      return;
    }
    const resetOk = await showConfirmPromise(`Reset Game ${gameNum} to 0-0? This cannot be undone.`, { title: 'Reset game', destructive: true });
    if (!resetOk) return;
    const setGameState = gameNum === 1 ? setGame1 : gameNum === 2 ? setGame2 : setGame3;
    const setServiceState = gameNum === 1 ? setGame1Service : gameNum === 2 ? setGame2Service : setGame3Service;
    const setPlayerScoresState = gameNum === 1 ? setGame1PlayerScores : gameNum === 2 ? setGame2PlayerScores : setGame3PlayerScores;
    const defaultService = { currentServer: 'player1', firstServer: 'player1', servesInRotation: 0, services: [] };
    const defaultServiceDoubles = match?.matchType === 'doubles' && match?.team1Players?.[0]
      ? { ...defaultService, currentServerPlayerId: match.team1Players[0], firstServerPlayerId: match.team1Players[0] }
      : defaultService;
    const newGameState = { player1: 0, player2: 0 };
    setGameState(newGameState);
    setServiceState(match?.matchType === 'doubles' ? defaultServiceDoubles : defaultService);
    setPlayerScoresState({});
    const filteredHistory = scoreHistory.filter(entry => entry.gameNum !== gameNum);
    setScoreHistory(filteredHistory);
    const gameKey = `game${gameNum}`;
    const newGameWinners = { ...gameWinners, [gameKey]: null };
    setGameWinners(newGameWinners);
    const matchResult = isMatchWon(newGameWinners);
    const newMatchWinner = matchResult.won ? matchResult.winner : null;
    setMatchWinner(newMatchWinner);
    const payload = {
      game1: gameNum === 1 ? newGameState : game1,
      game2: gameNum === 2 ? newGameState : game2,
      game3: gameNum === 3 ? newGameState : game3,
      currentGame,
      gameWinners: newGameWinners,
      matchWinner: newMatchWinner,
      game1Service: gameNum === 1 ? (match?.matchType === 'doubles' ? defaultServiceDoubles : defaultService) : game1Service,
      game2Service: gameNum === 2 ? (match?.matchType === 'doubles' ? defaultServiceDoubles : defaultService) : game2Service,
      game3Service: gameNum === 3 ? (match?.matchType === 'doubles' ? defaultServiceDoubles : defaultService) : game3Service,
      game1PlayerScores: gameNum === 1 ? {} : game1PlayerScores,
      game2PlayerScores: gameNum === 2 ? {} : game2PlayerScores,
      game3PlayerScores: gameNum === 3 ? {} : game3PlayerScores,
      scoreHistory: filteredHistory
    };
    await persistScoresToFirestore(payload);
    setUndoHistory([]);
    showToast(`Game ${gameNum} reset to 0-0.`, { variant: 'success' });
  }

  /** Admin only: set game score manually (offline entry). */
  async function handleSetManualScore(gameNum, p1Score, p2Score) {
    if (!isAdmin) return;
    const matchType = match?.matchType || 'singles';
    const scoringOptions = getScoringOptionsForMatchType(scoringConfig, matchType);
    const gameResult = isGameWon(Number(p1Score), Number(p2Score), matchType, { pointsToWin: scoringOptions.pointsToWin });
    const setGameState = gameNum === 1 ? setGame1 : gameNum === 2 ? setGame2 : setGame3;
    const setServiceState = gameNum === 1 ? setGame1Service : gameNum === 2 ? setGame2Service : setGame3Service;
    const setPlayerScoresState = gameNum === 1 ? setGame1PlayerScores : gameNum === 2 ? setGame2PlayerScores : setGame3PlayerScores;
    const newGameState = { player1: Number(p1Score) || 0, player2: Number(p2Score) || 0 };
    setGameState(newGameState);
    setPlayerScoresState({});
    const defaultService = { currentServer: 'player1', firstServer: 'player1', servesInRotation: 0, services: [] };
    const defaultServiceDoubles = match?.matchType === 'doubles' && match?.team1Players?.[0]
      ? { ...defaultService, currentServerPlayerId: match.team1Players[0], firstServerPlayerId: match.team1Players[0] }
      : defaultService;
    setServiceState(match?.matchType === 'doubles' ? defaultServiceDoubles : defaultService);
    const manualEntry = {
      gameNum,
      manual: true,
      player1Score: newGameState.player1,
      player2Score: newGameState.player2,
      refereeId: currentUser.uid,
      refereeName: getUserDisplayName({ displayName: userProfile?.displayName || currentUser.displayName, email: currentUser.email }),
      timestamp: Timestamp.now()
    };
    const filteredHistory = scoreHistory.filter(entry => entry.gameNum !== gameNum);
    const newScoreHistory = [...filteredHistory, manualEntry];
    setScoreHistory(newScoreHistory);
    const gameKey = `game${gameNum}`;
    const newGameWinners = gameResult.won ? { ...gameWinners, [gameKey]: gameResult.winner } : { ...gameWinners, [gameKey]: null };
    setGameWinners(newGameWinners);
    const matchResult = isMatchWon(newGameWinners);
    const newMatchWinner = matchResult.won ? matchResult.winner : matchWinner;
    setMatchWinner(newMatchWinner);
    const payload = {
      game1: gameNum === 1 ? newGameState : game1,
      game2: gameNum === 2 ? newGameState : game2,
      game3: gameNum === 3 ? newGameState : game3,
      currentGame,
      gameWinners: newGameWinners,
      matchWinner: newMatchWinner,
      game1Service: gameNum === 1 ? (match?.matchType === 'doubles' ? defaultServiceDoubles : defaultService) : game1Service,
      game2Service: gameNum === 2 ? (match?.matchType === 'doubles' ? defaultServiceDoubles : defaultService) : game2Service,
      game3Service: gameNum === 3 ? (match?.matchType === 'doubles' ? defaultServiceDoubles : defaultService) : game3Service,
      game1PlayerScores: gameNum === 1 ? {} : game1PlayerScores,
      game2PlayerScores: gameNum === 2 ? {} : game2PlayerScores,
      game3PlayerScores: gameNum === 3 ? {} : game3PlayerScores,
      scoreHistory: newScoreHistory
    };
    await persistScoresToFirestore(payload);
    setShowManualScoreModal(null);
  }

  async function handleEndMatch() {
    if (!matchWinner) {
      showAlert('Please complete the match first', { variant: 'warning' });
      return;
    }

    const endOk = await showConfirmPromise('End the match? This will mark it as completed.', { title: 'End match' });
    if (!endOk) return;

    setSaving(true);
    try {
      const scores = {
        game1,
        game2,
        game3,
        currentGame,
        gameWinners,
        matchWinner,
        game1Service,
        game2Service,
        game3Service,
        game1PlayerScores,
        game2PlayerScores,
        game3PlayerScores,
        scoreHistory,
        completedAt: Timestamp.now()
      };

      let winnerIds = [];
      let loserIds = [];
      
      if (match.matchType === 'doubles') {
        if (matchWinner === 'player1') {
          winnerIds = match.team1Players || [];
          loserIds = match.team2Players || [];
        } else {
          winnerIds = match.team2Players || [];
          loserIds = match.team1Players || [];
        }
      } else {
        winnerIds = [matchWinner === 'player1' ? match.player1Id : match.player2Id];
        loserIds = [matchWinner === 'player1' ? match.player2Id : match.player1Id];
      }
      
      const userName = getUserDisplayName({ displayName: userProfile?.displayName || currentUser.displayName, email: currentUser.email });
      const scoreParts = [`${game1.player1}-${game1.player2}`, `${game2.player1}-${game2.player2}`];
      if (game3.player1 + game3.player2 > 0) scoreParts.push(`${game3.player1}-${game3.player2}`);
      const scoreSummary = scoreParts.join(', ');
      await matchApi.matchComplete(matchId, {
        scores,
        winnerId: winnerIds[0],
        winnerIds,
        loserIds,
        scoreSummary,
      }, userName);

      const gameIdForCache = match.gameId || 'table-tennis';
      invalidateCache(CACHE_KEYS.schedule(gameIdForCache));
      invalidateCache(CACHE_KEYS.dashboard(gameIdForCache));
      invalidateCache(CACHE_KEYS.tournamentList(gameIdForCache));
      invalidateStatisticsCache(gameIdForCache);
      const profilePlayerIds = match.matchType === 'doubles'
        ? [...(match.team1Players || []), ...(match.team2Players || [])]
        : [match.player1Id, match.player2Id].filter(Boolean);
      profilePlayerIds.forEach((uid) => invalidatePlayerProfileCache(uid));

      if (await isEmailConfigured()) {
        const playerIds = match.matchType === 'doubles'
          ? [...(match.team1Players || []), ...(match.team2Players || [])]
          : [match.player1Id, match.player2Id].filter(Boolean);
        const players = await getPlayers(playerIds);
        const emails = [...new Set(Object.values(players).map(p => p?.email).filter(Boolean))];
        const summary = match.matchType === 'doubles'
          ? `${match.team1Name || 'Team 1'} vs ${match.team2Name || 'Team 2'}`
          : `${player1Name} vs ${player2Name}`;
        const scoreParts = [`${game1.player1}-${game1.player2}`, `${game2.player1}-${game2.player2}`];
        if (game3.player1 + game3.player2 > 0) scoreParts.push(`${game3.player1}-${game3.player2}`);
        const resultSummary = `${matchWinner === 'player1' ? player1Name : player2Name} def. ${matchWinner === 'player1' ? player2Name : player1Name} ${scoreParts.join(', ')}`;
        for (const email of emails) {
          await sendMatchCompleted(email, summary, resultSummary).catch(() => {});
        }
      }

      showToast('Match ended successfully!', { variant: 'success' });
      if (celebration?.checkAndShowCelebration && match.tournamentId) {
        await celebration.checkAndShowCelebration({ afterMatchComplete: true, tournamentId: match.tournamentId });
      }
      navigate('/schedule');
    } catch (err) {
      console.error('Error ending match:', err);
      showAlert('Error ending match: ' + err.message, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <DataFetchOverlay message="Loading match…" />;
  }

  if (!match) {
    return <div className="error">Match not found</div>;
  }

  const matchPlayerIds = new Set();
  if (match.matchType === 'doubles') {
    (match.team1Players || []).forEach(id => matchPlayerIds.add(id));
    (match.team2Players || []).forEach(id => matchPlayerIds.add(id));
  } else {
    if (match.player1Id) matchPlayerIds.add(match.player1Id);
    if (match.player2Id) matchPlayerIds.add(match.player2Id);
  }
  const isNonPlaying = currentUser && !matchPlayerIds.has(currentUser.uid);
  const isReferee = match.refereeId === currentUser?.uid;
  const isAdmin = userProfile?.isAdmin || userProfile?.isSuperAdmin;
  const isEditLocked = match?.status === 'completed';
  const canScore = !isEditLocked && (isReferee || isAdmin);
  const canStartMatch = !isEditLocked && !match.startedAt && (isReferee || isAdmin);
  const canAssignSelfAsReferee = !isEditLocked && isNonPlaying && match.refereeId !== currentUser?.uid;

  if (!currentUser) {
    return (
      <div className="error">
        <p>You must be signed in to view this match.</p>
        <button onClick={() => navigate('/schedule')} className="btn-secondary">
          Back to Schedule
        </button>
      </div>
    );
  }

  const gamesWon1 = Object.values(gameWinners).filter(w => w === 'player1').length;
  const gamesWon2 = Object.values(gameWinners).filter(w => w === 'player2').length;

  return (
    <div className="match-scoring-container">
      <header className="scoring-header">
        <div>
          <h1 className="page-title">Match Scoring</h1>
          <p className="page-subtitle">
            {match.matchType === 'doubles' ? 'Doubles' : 'Singles'} • Best of 3
          </p>
        </div>
        <div className="scoring-header-actions">
          <button onClick={() => setShowHistoryModal(true)} className="btn-secondary scoring-btn">
            History
          </button>
          <button onClick={() => navigate('/schedule')} className="btn-secondary scoring-btn">
            Back to Schedule
          </button>
          {canScore && match.startedAt && !isEditLocked && (
            <button
              onClick={handleEndMatch}
              disabled={!matchWinner || saving}
              className="btn-destructive scoring-btn"
            >
              {saving ? 'Ending…' : 'End Match'}
            </button>
          )}
        </div>
      </header>

      <div className="match-info card-ui scoring-card">
        <div className="match-status-bar">
          <div className="status-info">
            <span className="status-label">Status</span>
            <span className={`scoring-status-badge status-${match.status || 'scheduled'}`}>
              {match.status || 'scheduled'}
            </span>
          </div>
          {refereeName && (
            <div className="referee-info">
              <span className="referee-label">Referee</span>
              <span className="referee-name">{refereeName}</span>
            </div>
          )}
          {match.startedAt && (
            <div className="start-time">
              Started {formatDateTime(match.startedAt)}
            </div>
          )}
          {(venueDisplay.venueName || venueDisplay.courtName) && (
            <div className="match-venue-info">
              Venue: {[venueDisplay.venueName, venueDisplay.courtName].filter(Boolean).join(' · ') || '—'}
            </div>
          )}
          {currentUser && (
            <div className="scoring-viewer-count" title="Total unique users who have viewed this match score">
              <span className="scoring-viewer-icon" aria-hidden>👁</span>
              <span className="scoring-viewer-number">{match.viewCount ?? 0}</span>
              <span className="scoring-viewer-label">unique views</span>
            </div>
          )}
        </div>

        {isEditLocked && (
          <div className="scoring-view-only-banner" role="status">
            Match completed — view only. No changes can be made.
          </div>
        )}
        <div className="scoring-match-actions">
          {canStartMatch && (
            <button onClick={handleStartMatch} className="btn-primary scoring-btn">
              Start Match
            </button>
          )}
          {canAssignSelfAsReferee && (
            <button onClick={handleAssignSelfAsReferee} className="btn-secondary scoring-btn">
              Assign myself as referee
            </button>
          )}
        </div>

        <div className="scoring-players-summary">
          <div className="scoring-player-block">
            <div className="scoring-player-name">
              {match?.matchType === 'doubles' && (match?.team1Players || []).length >= 2 ? (
                <TeamStatsLink playerIds={match.team1Players} className="scoring-player-profile-link">{player1Name}</TeamStatsLink>
              ) : match?.player1Id ? (
                <ProfileLink userId={match.player1Id} className="scoring-player-profile-link">{player1Name}</ProfileLink>
              ) : (
                player1Name
              )}
            </div>
            {match?.matchType !== 'doubles' && getPlayerApartmentFlat(match?.player1Id) && (
              <div className="scoring-player-meta">{getPlayerApartmentFlat(match.player1Id)}</div>
            )}
            {match?.matchType === 'doubles' && (match?.team1Players || []).length > 0 && (
              <div className="team-members">
                {(match.team1Players || []).map((id, i) => (
                  <span key={id}>
                    {i > 0 && ' & '}
                    <ProfileLink userId={id} className="scoring-player-profile-link">{getPlayerName(id)}</ProfileLink>
                  </span>
                ))}
              </div>
            )}
            <div className="scoring-games-won">{gamesWon1}</div>
            <div className="scoring-games-won-label">Games won</div>
            {currentUser && (
              <div className="scoring-reactions-row scoring-reactions-in-block scoring-reactions-aggregated" aria-label={`Support ${player1Name}`}>
                <span className="scoring-reactions-label">Support</span>
                <div className="scoring-reactions-aggregated-inner">
                  {REACTION_EMOJIS.map((emoji) => (
                    <span key={emoji} className="scoring-reaction-aggregated-item">
                      <button
                        type="button"
                        onClick={() => toggleReaction(emoji, 'player1')}
                        className={`scoring-reaction-btn scoring-reaction-btn-sm ${myReactions.player1?.has(emoji) ? 'scoring-reaction-btn-active' : ''}`}
                        title={myReactions.player1?.has(emoji) ? 'Remove support' : `Support ${player1Name}`}
                        aria-pressed={myReactions.player1?.has(emoji)}
                      >
                        {emoji}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowReactionsModal(true)}
                        className="scoring-reaction-count scoring-reaction-count-sm"
                        title="See all reactions by team and emoji"
                      >
                        {reactionCounts.player1?.[String(REACTION_EMOJIS.indexOf(emoji))] ?? 0}
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="scoring-vs-pill">VS</div>
          <div className="scoring-player-block">
            <div className="scoring-player-name">
              {match?.matchType === 'doubles' && (match?.team2Players || []).length >= 2 ? (
                <TeamStatsLink playerIds={match.team2Players} className="scoring-player-profile-link">{player2Name}</TeamStatsLink>
              ) : match?.player2Id ? (
                <ProfileLink userId={match.player2Id} className="scoring-player-profile-link">{player2Name}</ProfileLink>
              ) : (
                player2Name
              )}
            </div>
            {match?.matchType !== 'doubles' && getPlayerApartmentFlat(match?.player2Id) && (
              <div className="scoring-player-meta">{getPlayerApartmentFlat(match.player2Id)}</div>
            )}
            {match?.matchType === 'doubles' && (match?.team2Players || []).length > 0 && (
              <div className="team-members">
                {(match.team2Players || []).map((id, i) => (
                  <span key={id}>
                    {i > 0 && ' & '}
                    <ProfileLink userId={id} className="scoring-player-profile-link">{getPlayerName(id)}</ProfileLink>
                  </span>
                ))}
              </div>
            )}
            <div className="scoring-games-won">{gamesWon2}</div>
            <div className="scoring-games-won-label">Games won</div>
            {currentUser && (
              <div className="scoring-reactions-row scoring-reactions-in-block scoring-reactions-aggregated" aria-label={`Support ${player2Name}`}>
                <span className="scoring-reactions-label">Support</span>
                <div className="scoring-reactions-aggregated-inner">
                  {REACTION_EMOJIS.map((emoji) => (
                    <span key={emoji} className="scoring-reaction-aggregated-item">
                      <button
                        type="button"
                        onClick={() => toggleReaction(emoji, 'player2')}
                        className={`scoring-reaction-btn scoring-reaction-btn-sm ${myReactions.player2?.has(emoji) ? 'scoring-reaction-btn-active' : ''}`}
                        title={myReactions.player2?.has(emoji) ? 'Remove support' : `Support ${player2Name}`}
                        aria-pressed={myReactions.player2?.has(emoji)}
                      >
                        {emoji}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowReactionsModal(true)}
                        className="scoring-reaction-count scoring-reaction-count-sm"
                        title="See all reactions by team and emoji"
                      >
                        {reactionCounts.player2?.[String(REACTION_EMOJIS.indexOf(emoji))] ?? 0}
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        {match.matchType === 'doubles' && (
          <div className="match-type-badge">Doubles Match</div>
        )}
      </div>

      {(match.startedAt || isEditLocked) && (
      <>
      <div className={`umpire-games ${isEditLocked ? 'umpire-games-view-only' : ''}`}>
        {[1, 2, 3].map(gameNum => {
          const gameState = gameNum === 1 ? game1 : gameNum === 2 ? game2 : game3;
          const serviceState = gameNum === 1 ? game1Service : gameNum === 2 ? game2Service : game3Service;
          const gameKey = `game${gameNum}`;
          const gameWinner = gameWinners[gameKey];
          const isGameComplete = gameWinner !== null;
          const serving1 = serviceState.currentServer === 'player1';
          const serving2 = serviceState.currentServer === 'player2';

          const lastScoreEntry = scoreHistory
            .filter(entry => entry.gameNum === gameNum)
            .slice(-1)[0];

          return (
            <div
              key={gameNum}
              className={`umpire-game-card card-ui ${isGameComplete ? 'umpire-game-completed' : ''}`}
            >
              <div className="umpire-game-title-row">
                <h3 className="umpire-game-title">
                  Game {gameNum}
                  {isGameComplete && (
                    <span className="umpire-game-winner">
                      {' — '}{gameWinner === 'player1' ? player1Name : player2Name} wins
                    </span>
                  )}
                </h3>
                <button
                  type="button"
                  onClick={() => setShowGameInfo({ ...showGameInfo, [`game${gameNum}`]: true })}
                  className="btn-secondary umpire-game-info-btn"
                >
                  Game Info
                </button>
              </div>

              {canScore && match.startedAt && !isGameComplete && gameState.player1 === 0 && gameState.player2 === 0 && (
                <div className="umpire-first-server-row">
                  <span className="umpire-first-server-label">First server (toss):</span>
                  <button
                    type="button"
                    onClick={() => handleSetFirstServer(gameNum, 'player1')}
                    className={`btn-secondary umpire-first-server-btn ${serviceState.currentServer === 'player1' ? 'active' : ''}`}
                  >
                    {player1Name} serves first
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSetFirstServer(gameNum, 'player2')}
                    className={`btn-secondary umpire-first-server-btn ${serviceState.currentServer === 'player2' ? 'active' : ''}`}
                  >
                    {player2Name} serves first
                  </button>
                </div>
              )}

              {isAdmin && match.startedAt && !isEditLocked && (
                <div className="umpire-admin-row">
                  <button
                    type="button"
                    onClick={() => handleResetGame(gameNum)}
                    className="btn-secondary umpire-admin-btn"
                  >
                    Reset game
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowManualScoreModal({ gameNum });
                      setManualScoreP1(gameState.player1);
                      setManualScoreP2(gameState.player2);
                    }}
                    className="btn-secondary umpire-admin-btn"
                  >
                    Set score manually (offline)
                  </button>
                </div>
              )}

              <div className="umpire-game-grid">
                <div
                  className={`umpire-player-side ${serving1 ? 'umpire-serving' : ''}`}
                  data-serving={serving1}
                >
                  {serving1 && <div className="umpire-serving-badge">SERVING</div>}
                  <div className="umpire-side-name">{player1Name}</div>
                  <div className="umpire-score-big">{gameState.player1}</div>
                  {lastScoreEntry && lastScoreEntry.player === 'player1' && !isEditLocked && (
                    <div className="umpire-referee-hint">Ref: {lastScoreEntry.refereeName}</div>
                  )}
                  {!isEditLocked && (
                    <>
                      <button
                        type="button"
                        onClick={() => confirmScoreUpdate(gameNum, 'player1', 1)}
                        disabled={isGameComplete || !canScore || !match.startedAt}
                        className="umpire-add-point-btn"
                      >
                        +1
                      </button>
                      <div className="umpire-side-actions">
                        <button
                          type="button"
                          onClick={() => confirmScoreUpdate(gameNum, 'player1', -1)}
                          disabled={isGameComplete || gameState.player1 === 0 || !canScore || !match.startedAt}
                          className="btn-secondary umpire-correct-btn"
                        >
                          Correct
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <div
                  className={`umpire-player-side ${serving2 ? 'umpire-serving' : ''}`}
                  data-serving={serving2}
                >
                  {serving2 && <div className="umpire-serving-badge">SERVING</div>}
                  <div className="umpire-side-name">{player2Name}</div>
                  <div className="umpire-score-big">{gameState.player2}</div>
                  {lastScoreEntry && lastScoreEntry.player === 'player2' && !isEditLocked && (
                    <div className="umpire-referee-hint">Ref: {lastScoreEntry.refereeName}</div>
                  )}
                  {!isEditLocked && (
                    <>
                      <button
                        type="button"
                        onClick={() => confirmScoreUpdate(gameNum, 'player2', 1)}
                        disabled={isGameComplete || !canScore || !match.startedAt}
                        className="umpire-add-point-btn"
                      >
                        +1
                      </button>
                      <div className="umpire-side-actions">
                        <button
                          type="button"
                          onClick={() => confirmScoreUpdate(gameNum, 'player2', -1)}
                          disabled={isGameComplete || gameState.player2 === 0 || !canScore || !match.startedAt}
                          className="btn-secondary umpire-correct-btn"
                        >
                          Correct
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {matchWinner && (
        <div className="match-winner-banner">
          <h2 className="match-winner-title">
            Match Winner: {matchWinner === 'player1' ? player1Name : player2Name}
          </h2>
        </div>
      )}

      <div className="scoring-actions">
        {canScore && match.startedAt && isReferee && (
          <button
            type="button"
            onClick={handleUndo}
            disabled={undoHistory.length === 0}
            className="btn-secondary scoring-btn"
          >
            Undo Last Score
          </button>
        )}
        {canScore && match.startedAt && !isEditLocked && (
          <button
            type="button"
            onClick={handleEndMatch}
            disabled={!matchWinner || saving}
            className="btn-primary scoring-btn"
          >
            {saving ? 'Ending…' : 'End Match'}
          </button>
        )}
      </div>
      </>
      )}

      {/* Player Selection Modal for Doubles */}
      {pendingScoreUpdate && pendingScoreUpdate.needsPlayerSelection && match?.matchType === 'doubles' && (
        <div className="modal-overlay" onClick={() => setPendingScoreUpdate(null)}>
          <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Select Which Player Scored</h3>
            <p>
              Game {pendingScoreUpdate.gameNum}, {' '}
              {pendingScoreUpdate.player === 'player1' ? player1Name : player2Name}
            </p>
            <div className="player-selection" style={{ marginBottom: '15px' }}>
              {(pendingScoreUpdate.player === 'player1' ? match.team1Players : match.team2Players)?.map((playerId) => {
                const playerName = getPlayerName(playerId);
                return (
                  <button
                    key={playerId}
                    onClick={() => {
                      setPendingScoreUpdate({
                        ...pendingScoreUpdate,
                        scoringPlayerId: playerId,
                        needsPlayerSelection: false
                      });
                    }}
                    className="btn-primary"
                    style={{ margin: '5px 0', display: 'block', width: '100%', padding: '10px' }}
                  >
                    {playerName}
                  </button>
                );
              })}
            </div>
            <div className="modal-actions">
              <button onClick={() => setPendingScoreUpdate(null)} className="btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {pendingScoreUpdate && !pendingScoreUpdate.needsPlayerSelection && (
        <div className="modal-overlay" onClick={() => setPendingScoreUpdate(null)}>
          <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Score Update</h3>
            <p>
              Update score for Game {pendingScoreUpdate.gameNum}, {' '}
              {pendingScoreUpdate.player === 'player1' ? player1Name : player2Name} {' '}
              by {pendingScoreUpdate.increment > 0 ? '+' : ''}{pendingScoreUpdate.increment}?
              {pendingScoreUpdate.scoringPlayerId && match?.matchType === 'doubles' && (
                <span> (Player: {getPlayerName(pendingScoreUpdate.scoringPlayerId)})</span>
              )}
            </p>
            <div className="modal-actions">
              <button onClick={executeScoreUpdate} className="btn-primary">
                Confirm
              </button>
              <button onClick={() => setPendingScoreUpdate(null)} className="btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin: Set score manually (offline entry) */}
      {showManualScoreModal && (
        <div className="modal-overlay" onClick={() => setShowManualScoreModal(null)}>
          <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Set score manually — Game {showManualScoreModal.gameNum}</h3>
            <p className="manual-score-hint">Enter final score for this game (e.g. offline match).</p>
            <div className="manual-score-inputs">
              <label>
                <span>{player1Name}</span>
                <input
                  type="number"
                  min="0"
                  max={getScoringOptionsForMatchType(scoringConfig, match?.matchType || 'singles').pointsToWin}
                  value={manualScoreP1}
                  onChange={(e) => setManualScoreP1(e.target.value)}
                />
              </label>
              <label>
                <span>{player2Name}</span>
                <input
                  type="number"
                  min="0"
                  max={getScoringOptionsForMatchType(scoringConfig, match?.matchType || 'singles').pointsToWin}
                  value={manualScoreP2}
                  onChange={(e) => setManualScoreP2(e.target.value)}
                />
              </label>
            </div>
            <div className="modal-actions">
              <button
                onClick={() => handleSetManualScore(showManualScoreModal.gameNum, manualScoreP1, manualScoreP2)}
                className="btn-primary"
              >
                Save
              </button>
              <button onClick={() => setShowManualScoreModal(null)} className="btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Game Info Modals */}
      {showGameInfo.game1 && (
        <GameInfoModal
          gameNum={1}
          gameState={game1}
          serviceState={game1Service}
          playerScores={game1PlayerScores}
          player1Name={player1Name}
          player2Name={player2Name}
          player1Id={match?.player1Id}
          player2Id={match?.player2Id}
          team1Players={match?.team1Players}
          team2Players={match?.team2Players}
          matchType={match?.matchType}
          onClose={() => setShowGameInfo({ ...showGameInfo, game1: false })}
        />
      )}
      {showGameInfo.game2 && (
        <GameInfoModal
          gameNum={2}
          gameState={game2}
          serviceState={game2Service}
          playerScores={game2PlayerScores}
          player1Name={player1Name}
          player2Name={player2Name}
          player1Id={match?.player1Id}
          player2Id={match?.player2Id}
          team1Players={match?.team1Players}
          team2Players={match?.team2Players}
          matchType={match?.matchType}
          onClose={() => setShowGameInfo({ ...showGameInfo, game2: false })}
        />
      )}
      {showGameInfo.game3 && (
        <GameInfoModal
          gameNum={3}
          gameState={game3}
          serviceState={game3Service}
          playerScores={game3PlayerScores}
          player1Name={player1Name}
          player2Name={player2Name}
          player1Id={match?.player1Id}
          player2Id={match?.player2Id}
          team1Players={match?.team1Players}
          team2Players={match?.team2Players}
          matchType={match?.matchType}
          onClose={() => setShowGameInfo({ ...showGameInfo, game3: false })}
        />
      )}
      {showHistoryModal && (
        <MatchUpdateHistoryModal
          matchId={matchId}
          onClose={() => setShowHistoryModal(false)}
        />
      )}
      {showReactionsModal && (
        <MatchReactionsModal
          matchId={matchId}
          player1Name={player1Name}
          player2Name={player2Name}
          onClose={() => setShowReactionsModal(false)}
        />
      )}
    </div>
  );
}
